/**
 * RenameManuscriptDialog.jsx
 * Modal dialog for renaming the currently open manuscript.
 * Triggered via File → Rename Manuscript… (loom:open-manuscript-rename event)
 * or any caller that opens it directly via the `open` prop.
 */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Edit3, Loader2 } from 'lucide-react';
import { usePW } from '@/lib/store';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

export function RenameManuscriptDialog({ open, onOpenChange }) {
  const { project, updateProject } = usePW();
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  // Seed the input with the current project title whenever dialog opens
  useEffect(() => {
    if (open && project) {
      const current = project.title || project.name || '';
      setDraft(current);
      // Auto-select all text so the user can type immediately
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.select();
        }
      }, 80);
    }
  }, [open, project]);

  const handleSave = async () => {
    const next = draft.trim().slice(0, 200);
    if (!next) { toast.error('Please enter a name.'); return; }
    const current = project?.title || project?.name || '';
    if (next === current) { onOpenChange(false); return; }
    setSaving(true);
    try {
      await axios.post(
        `${API}/projects/${project.id}/global-rename`,
        { title: next },
        { withCredentials: true },
      );
      if (updateProject) await updateProject({ title: next, name: next });
      toast.success(`Renamed to "${next}"`);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Rename failed — please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); handleSave(); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md"
        data-testid="rename-manuscript-dialog"
      >
        <DialogHeader className="border-b border-border pb-3">
          <DialogTitle className="flex items-center gap-2 text-[14px]">
            <Edit3 className="h-4 w-4 text-primary" />
            Rename Manuscript
          </DialogTitle>
          <DialogDescription className="text-[12px]">
            Enter a new title for <span className="font-medium text-foreground">{project?.title || project?.name || 'this manuscript'}</span>.
            All references will be updated.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <Label htmlFor="rename-input" className="text-[11.5px]">New title</Label>
          <Input
            id="rename-input"
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            maxLength={200}
            placeholder="Enter manuscript title…"
            className="mt-1.5 h-9 text-[13px]"
            data-testid="rename-manuscript-input"
            disabled={saving}
          />
          <p className="mt-1 text-[10.5px] text-muted-foreground">{draft.length}/200 characters</p>
        </div>

        <DialogFooter className="border-t border-border pt-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            data-testid="rename-manuscript-cancel"
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={saving || !draft.trim()}
            data-testid="rename-manuscript-save"
          >
            {saving && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default RenameManuscriptDialog;
